import React, { useMemo } from 'react'
import { DownloadsCategoryModel, DownloadsFilter } from '../models/downloads.model'
import CategoryNode from 'src/Libs/downloads/components/CategoryNode'
import CategoryDetails from './CategoryDetails'
import { Button, Stack } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import DocumentNode from './DownloadItemNode'
import { getFilesAndCategories } from '../utils'
import useModals from 'src/Hooks/useModals'
import { useCategoryDisplayName } from '../hooks'
import { BlankView, BlankViewImageVariants } from 'src/Components/blank-view'

interface Props {
  category: DownloadsCategoryModel
  isServiceUser?: boolean
  filters: DownloadsFilter
  chosenCategoryId?: number
  favoriteFileIds: number[]
  setChosenCategoryId: (id?: number) => void
}

/**
 * Displays a list of downloads in various levels of categories and items
 */
const DownloadsList: React.FC<Props> = ({ category, isServiceUser, filters, chosenCategoryId, favoriteFileIds, setChosenCategoryId }) => {
  const { t } = useCustomTranslation()
  const getCategoryDisplayName = useCategoryDisplayName()
  const { openDownloadCategoryEditor } = useModals()
  const isRootLevel = category.layer === 1
  const showAddCategoryButton = isRootLevel && isServiceUser

  const { files, categories } = useMemo(() => {
    return getFilesAndCategories(category.children)
  }, [category])

  const selectCategory = (categoryId?: number): void => {
    setChosenCategoryId(categoryId)
  }

  const parentObject = useMemo(() => {
    return {
      id: category.id,
      name: getCategoryDisplayName(category.nameLanguages),
      nameLanguages: category.nameLanguages,
      layer: category.layer,
      pathToken: category.pathToken,
      path: category.path,
      visibleToCustomer: category.visibleToCustomer,
      visibleToInstaller: category.visibleToInstaller,
    }
  }, [category, getCategoryDisplayName])

  if (chosenCategoryId) {
    return (
      <CategoryDetails
        favoriteFileIds={favoriteFileIds}
        category={category}
        isServiceUser={isServiceUser}
        filters={filters}
        selectCategory={selectCategory}
      />
    )
  }

  if (!files.length && !categories.length) {
    return (
      <BlankView
        imageVariant={BlankViewImageVariants.Neutral}
        title={t('downloads.no-category-found')}
        action={
          isRootLevel && isServiceUser
            ? { text: t('documents.add-category.title'), onClick: () => openDownloadCategoryEditor({ isFirstLevel: true, selectCategory }) }
            : undefined
        }
      />
    )
  }

  return (
    <Stack direction={'column'} spacing={2} sx={{ mb: 2 }}>
      {categories.map(item => {
        return (
          <CategoryNode
            key={item.id}
            category={item}
            isRootLevel={isRootLevel}
            isServiceUser={isServiceUser}
            onSelectCategory={selectCategory}
            parent={parentObject}
            sx={{ borderColor: isRootLevel ? 'background.paper' : 'grey.grey06', backgroundColor: isRootLevel ? 'background.paper' : 'grey.grey07' }}
          />
        )
      })}
      {files.map(file => (
        <DocumentNode
          favoriteFileIds={favoriteFileIds}
          key={`file-${file.id}`}
          file={file}
          isServiceUser={isServiceUser}
          selectCategory={selectCategory}
          directParent={category}
        />
      ))}
      {showAddCategoryButton && (
        <Button
          color="primary"
          variant="contained"
          sx={{ mt: 2, width: 'fit-content' }}
          onClick={() =>
            openDownloadCategoryEditor({
              isFirstLevel: true,
              selectCategory,
            })
          }
          data-qa={'button-add-category'}
        >
          {t('documents.add-category.title')}
        </Button>
      )}
    </Stack>
  )
}

export default DownloadsList
